<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { router } from "@/router/router";
import { useCampaignReport } from "@/composables/useReport";
import type { ExportTask, ReportFilters, ReportPreview } from "@/api/report";

const campaignId = computed(() =>
  String(router.currentRoute.value.params.campaign_id ?? ""),
);
const {
  draft,
  appliedFilters,
  preview,
  previewError,
  isLoadingPreview,
  evidencePage,
  evidencePages,
  validationError,
  hasPendingFilters,
  canExport,
  applyFilters,
  loadPreview,
  reports,
  page,
  totalPages,
  isLoadingList,
  listError,
  loadReports,
  setPage,
  error,
  isGenerating,
  downloading,
  pollError,
  integrityStatus,
  reportStatusBadge,
  startGeneration,
  startDownload,
} = useCampaignReport(campaignId);
const typeOptions = ref<ReportPreview["types"]>([]);
const agentOptions = ref<string[]>([]);
watch(preview, (value) => {
  if (!value) return;
  typeOptions.value = value.types;
  agentOptions.value = [
    ...new Set([...agentOptions.value, ...value.agents.map((a) => a.key)]),
  ];
});
watch(campaignId, () => {
  typeOptions.value = [];
  agentOptions.value = [];
  sourceDialog.value?.close();
  selected.value = null;
});
const selected = ref<ReportPreview["records"][number] | null>(null);
const sourceDialog = ref<HTMLDialogElement>();
async function openSource(record: ReportPreview["records"][number]) {
  selected.value = record;
  await nextTick();
  sourceDialog.value?.showModal();
}
const label = (key: string) =>
  typeOptions.value.find((t) => t.key === key)?.label ?? key;
const number = (value: number | undefined | null) =>
  value == null || !Number.isFinite(value)
    ? "Không rõ"
    : value.toLocaleString("vi-VN");
const time = (value?: number) =>
  !value
    ? "Không rõ"
    : new Date(value).toISOString().replace("T", " ").slice(0, 19) + " UTC";
function scopeText(filters?: ReportFilters) {
  if (!filters) return "Toàn chiến dịch (báo cáo cũ)";
  return [
    filters.from ? new Date(filters.from).toISOString().slice(0, 10) : "Từ đầu",
    filters.to
      ? "đến " + new Date(filters.to - 1).toISOString().slice(0, 10) + " UTC"
      : "đến nay",
    filters.data_type ? label(filters.data_type) : "Mọi loại",
    filters.agent_id ?? "Mọi máy",
    filters.search ? "Tìm: " + filters.search : "",
  ]
    .filter(Boolean)
    .join(" · ");
}
const integrityTask = computed(() =>
  reports.value.find(
    (t) =>
      t.integrity &&
      ["campaign_id", "from", "to", "data_type", "agent_id", "search"].every(
        (key) =>
          t.report_filters?.[key as keyof ReportFilters] ===
          appliedFilters.value[key as keyof ReportFilters],
      ),
  ),
);
const rankedAgents = computed(() =>
  [...(preview.value?.agents ?? [])].sort(
    (a, b) => b.count - a.count || a.key.localeCompare(b.key),
  ),
);
const agentGroups = computed(() => [
  { title: "Máy có nhiều bản ghi nhất", rows: rankedAgents.value.slice(0, 5) },
  {
    title: "Máy có ít bản ghi nhất",
    rows: rankedAgents.value.slice(-5).reverse(),
  },
]);
const trendMax = computed(
  () =>
    preview.value?.trend.reduce((max, item) => Math.max(max, item.count), 1) ??
    1,
);
const trendRows = computed(() => {
  const rows = preview.value?.trend ?? [];
  const first = Date.parse(rows[0]?.key ?? "");
  const span = Date.parse(rows.at(-1)?.key ?? "") - first;
  return rows.map((item) => ({
    ...item,
    x: span ? 50 + ((Date.parse(item.key) - first) / span) * 530 : 315,
    y: 150 - (item.count / trendMax.value) * 120,
  }));
});
const typeMax = computed(
  () =>
    preview.value?.summary.reduce(
      (max, item) => Math.max(max, item.count),
      1,
    ) ?? 1,
);
async function retry(task: ExportTask) {
  if (!canExport.value) return;
  const scope = task.report_filters;
  const id = campaignId.value;
  draft.value = {
    from: scope?.from ? new Date(scope.from).toISOString().slice(0, 10) : "",
    to: scope?.to ? new Date(scope.to - 1).toISOString().slice(0, 10) : "",
    data_type: scope?.data_type ?? "",
    agent_id: scope?.agent_id ?? "",
    search: scope?.search ?? "",
  };
  appliedFilters.value = { ...scope, campaign_id: id };
  await loadPreview(1);
  if (id === campaignId.value)
    await startGeneration(
      task.format === "csv" ? "csv" : task.format === "json" ? "json" : "pdf",
    );
}
</script>

<template>
  <main class="report-page">
    <header class="report-header">
      <div>
        <p class="eyebrow">CAMPAIGN INTELLIGENCE · UTC</p>
        <h1>Báo cáo chiến dịch</h1>
        <p>Tổng quan điều hành và bằng chứng dành cho chuyên viên.</p>
      </div>
      <div class="exports" aria-label="Xuất báo cáo">
        <button
          v-for="format in ['pdf', 'csv', 'json'] as const"
          :key="format"
          class="btn btn-sm"
          :class="format === 'pdf' ? 'btn-primary' : 'btn-outline'"
          :disabled="!canExport"
          @click="startGeneration(format)"
        >
          Xuất {{ format.toUpperCase() }}
        </button>
      </div>
    </header>
    <p class="report-note">
      Bản ghi là dữ liệu thu thập, không phải số lệnh đã gửi hay số lần thực
      thi. PDF gồm biểu đồ; CSV / JSON xuất dữ liệu nguồn.
    </p>

    <form class="filter-panel" @submit.prevent="applyFilters()">
      <label
        >Từ ngày (UTC)<input
          v-model="draft.from"
          class="input input-bordered"
          type="date"
          :max="draft.to || undefined"
          aria-label="Từ ngày (UTC)"
      /></label>
      <label
        >Đến hết ngày (UTC)<input
          v-model="draft.to"
          class="input input-bordered"
          type="date"
          :min="draft.from || undefined"
          aria-label="Đến hết ngày (UTC)"
      /></label>
      <label
        >Loại dữ liệu<select
          v-model="draft.data_type"
          class="select select-bordered"
          aria-label="Loại dữ liệu"
        >
          <option value="">Mọi loại</option>
          <option v-for="type in typeOptions" :key="type.key" :value="type.key">
            {{ type.label }}
          </option>
        </select></label
      >
      <label
        >Máy trạm<select
          v-model="draft.agent_id"
          class="select select-bordered"
          aria-label="Máy trạm"
        >
          <option value="">Mọi máy</option>
          <option
            v-if="draft.agent_id && !agentOptions.includes(draft.agent_id)"
            :value="draft.agent_id"
          >
            {{ draft.agent_id }}
          </option>
          <option v-for="id in agentOptions" :key="id" :value="id">
            {{ id }}
          </option>
        </select></label
      >
      <label class="search-filter"
        >Tìm trong bằng chứng<input
          v-model="draft.search"
          class="input input-bordered"
          type="search"
          placeholder="ID, máy trạm, nội dung nguồn…"
          aria-label="Tìm trong bằng chứng"
      /></label>
      <button
        class="btn btn-primary"
        type="submit"
        :disabled="!!validationError || isLoadingPreview"
      >
        Áp dụng bộ lọc
      </button>
      <p v-if="validationError" class="text-error filter-feedback" role="alert">
        {{ validationError }}
      </p>
      <p v-else-if="hasPendingFilters" class="filter-feedback">
        Bộ lọc chưa áp dụng. Xuất tệp tạm khóa để tránh sai phạm vi.
      </p>
    </form>
    <div class="applied-scope">
      <span>PHẠM VI ĐÃ ÁP DỤNG</span>
      <p>{{ scopeText(appliedFilters) }}</p>
    </div>
    <p v-if="isLoadingPreview" class="state-panel" role="status">
      Đang tải dữ liệu báo cáo…
    </p>
    <div v-else-if="previewError" class="state-panel" role="alert">
      <p>{{ previewError }}</p>
      <button class="btn btn-sm btn-outline" @click="loadPreview(evidencePage)">
        Thử lại dữ liệu
      </button>
    </div>
    <template v-else-if="preview">
      <section class="kpi-grid" aria-label="Chỉ số tổng quan">
        <article class="kpi">
          <span>Bản ghi thu thập</span
          ><strong>{{ number(preview.total_records) }}</strong
          ><small>Trong phạm vi bộ lọc</small>
        </article>
        <article class="kpi">
          <span>Máy có dữ liệu</span
          ><strong>{{ number(preview.total_agents) }}</strong
          ><small>Không phải tổng máy đã triển khai</small>
        </article>
        <article class="kpi">
          <span>Loại dữ liệu có bản ghi</span
          ><strong>{{
            preview.summary.filter((t) => t.count > 0).length
          }}</strong
          ><small>Trên {{ typeOptions.length }} loại hỗ trợ</small>
        </article>
        <article class="kpi">
          <span>Toàn vẹn báo cáo</span
          ><strong>{{
            integrityTask?.integrity
              ? number(integrityTask.integrity.score) + "/100"
              : "Chưa kiểm tra"
          }}</strong
          ><small>{{
            integrityTask?.integrity
              ? integrityStatus(integrityTask.integrity).label
              : "Chỉ có sau khi tạo báo cáo cùng phạm vi"
          }}</small>
        </article>
      </section>
      <section class="chart-grid" aria-label="Phân tích dữ liệu">
        <article class="panel">
          <h2>Xu hướng bản ghi</h2>
          <p class="muted">Theo ngày UTC · không gồm thời gian không rõ</p>
          <div v-if="preview.trend.length" class="trend-chart">
            <svg
              viewBox="0 0 600 180"
              role="img"
              aria-label="Xu hướng bản ghi theo ngày UTC"
            >
              <line
                x1="20"
                y1="150"
                x2="580"
                y2="150"
                stroke="currentColor"
                opacity=".2"
              />
              <g v-for="item in trendRows" :key="item.key">
                <line
                  :x1="item.x"
                  y1="150"
                  :x2="item.x"
                  :y2="item.y"
                  stroke="#2563eb"
                  stroke-width="3"
                />
                <circle :cx="item.x" :cy="item.y" r="4" fill="#2563eb">
                  <title>
                    {{ item.key }}: {{ number(item.count) }} bản ghi
                  </title>
                </circle>
              </g>
              <g font-size="11" fill="currentColor">
                <text x="35" y="33" text-anchor="end">
                  {{ number(trendMax) }}
                </text>
                <text x="35" y="153" text-anchor="end">0</text>
                <text x="50" y="175">{{ preview.trend[0]?.key }}</text>
                <text x="580" y="175" text-anchor="end">
                  {{ preview.trend.at(-1)?.key }}
                </text>
              </g>
            </svg>
            <details>
              <summary>
                Xem số liệu theo ngày ({{ preview.trend.length }})
              </summary>
              <dl class="daily-values">
                <template v-for="item in preview.trend" :key="item.key"
                  ><dt>{{ item.key }}</dt>
                  <dd>{{ number(item.count) }}</dd></template
                >
              </dl>
            </details>
          </div>
          <p v-else class="empty-chart">
            Chưa có bản ghi có thời gian xác định.
          </p>
        </article>
        <article class="panel">
          <h2>Phân bố loại dữ liệu</h2>
          <p class="muted">Số bản ghi theo loại</p>
          <ul v-if="preview.summary.length" class="distribution">
            <li v-for="item in preview.summary" :key="item.key">
              <div>
                <span>{{ label(item.key) }}</span
                ><b>{{ number(item.count) }}</b>
              </div>
              <meter
                :value="item.count"
                min="0"
                :max="typeMax"
                :aria-label="label(item.key) + ' — bản ghi'"
              >
                {{ item.count }}
              </meter>
            </li>
          </ul>
          <p v-else class="empty-chart">Chưa có dữ liệu theo loại.</p>
        </article>
        <article v-for="group in agentGroups" :key="group.title" class="panel">
          <h2>{{ group.title }}</h2>
          <p class="muted">Chỉ xếp hạng máy có dữ liệu trong phạm vi</p>
          <ol v-if="group.rows.length" class="agent-rank">
            <li v-for="agent in group.rows" :key="agent.key">
              <span :title="agent.key">{{ agent.key }}</span
              ><b>{{ number(agent.count) }}</b>
            </li>
          </ol>
          <p v-else class="empty-chart">Chưa có dữ liệu máy trạm.</p>
        </article>
      </section>
      <section class="panel evidence">
        <div class="section-heading">
          <div>
            <h2>Bằng chứng nguồn</h2>
            <p class="muted">
              {{ number(preview.total_filter) }} bản ghi · tìm kiếm theo bộ lọc
              phía trên
            </p>
          </div>
          <span>Trang {{ evidencePage }} / {{ evidencePages }}</span>
        </div>
        <p v-if="!preview.records.length" class="empty-chart">
          Không có bằng chứng trong phạm vi đã chọn.
        </p>
        <div v-else class="table-scroll">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">ID bản ghi</th>
                <th scope="col">Loại dữ liệu</th>
                <th scope="col">Máy trạm</th>
                <th scope="col">Thu thập (UTC)</th>
                <th scope="col">Nguồn</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="record in preview.records"
                :key="record.data_type + record.id"
              >
                <td class="identifier">{{ record.id }}</td>
                <td>{{ label(record.data_type) }}</td>
                <td class="identifier">{{ record.agent_id || "Không rõ" }}</td>
                <td>{{ time(record.created) }}</td>
                <td>
                  <button
                    class="btn btn-sm btn-outline"
                    :aria-label="'Xem nguồn ' + record.id"
                    @click="openSource(record)"
                  >
                    Xem nguồn
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <nav class="pager" aria-label="Phân trang bằng chứng">
          <button
            class="btn btn-sm btn-outline"
            :disabled="evidencePage <= 1 || hasPendingFilters"
            @click="loadPreview(evidencePage - 1)"
          >
            Bằng chứng trước</button
          ><button
            class="btn btn-sm btn-outline"
            :disabled="evidencePage >= evidencePages || hasPendingFilters"
            @click="loadPreview(evidencePage + 1)"
          >
            Bằng chứng sau
          </button>
        </nav>
      </section>
    </template>
    <section class="panel history">
      <div class="section-heading">
        <div>
          <h2>Lịch sử xuất báo cáo</h2>
          <p class="muted">
            Tiến độ tự khôi phục sau khi tải lại trang. Xuất báo cáo yêu cầu
            quyền quản lý.
          </p>
        </div>
        <button
          class="btn btn-sm btn-outline"
          :disabled="isLoadingList"
          @click="loadReports()"
        >
          Làm mới lịch sử
        </button>
      </div>
      <p v-if="isGenerating" role="status">Đang khởi tạo báo cáo…</p>
      <p v-if="error" class="text-error" role="alert">{{ error }}</p>
      <p v-if="pollError" role="alert">{{ pollError }}</p>
      <div v-if="listError" role="alert">
        <p>{{ listError }}</p>
        <button class="btn btn-sm btn-outline" @click="loadReports()">
          Thử lại lịch sử
        </button>
      </div>
      <p v-if="isLoadingList">Đang tải lịch sử…</p>
      <p v-else-if="!reports.length && !listError" class="empty-chart">
        Chưa có báo cáo được tạo.
      </p>
      <div v-if="reports.length" class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th scope="col">Tạo lúc / phạm vi</th>
              <th scope="col">Định dạng</th>
              <th scope="col">Trạng thái / tiến độ</th>
              <th scope="col">Toàn vẹn</th>
              <th scope="col">Bản ghi xuất</th>
              <th scope="col">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in reports" :key="task.id">
              <td class="history-scope">
                {{ time(task.created)
                }}<small>{{ scopeText(task.report_filters) }}</small>
              </td>
              <td>{{ (task.format || "pdf").toUpperCase() }}</td>
              <td>
                <span
                  class="badge"
                  :class="reportStatusBadge(task.status).cls"
                  >{{ reportStatusBadge(task.status).label }}</span
                >
                <div
                  v-if="
                    task.status === 'pending' || task.status === 'processing'
                  "
                  class="task-progress"
                >
                  <progress
                    class="progress progress-primary"
                    max="100"
                    :value="task.progress_percent"
                    :aria-label="'Tiến độ ' + task.id"
                  ></progress
                  >{{ number(task.progress_percent) }}%
                </div>
                <p v-if="task.error" class="text-error">{{ task.error }}</p>
                <small v-if="task.attempts">{{ task.attempts }} lần thử</small>
              </td>
              <td>
                {{
                  task.integrity
                    ? integrityStatus(task.integrity).label +
                      " · " +
                      number(task.integrity.score) +
                      "/100"
                    : "Chưa kiểm tra"
                }}
                <details v-if="task.integrity?.findings?.length">
                  <summary>Phát hiện</summary>
                  <ul>
                    <li
                      v-for="(finding, i) in task.integrity.findings"
                      :key="i"
                    >
                      {{ finding.check }}: {{ finding.message }}
                    </li>
                  </ul>
                </details>
              </td>
              <td>
                {{ number(task.exported_records) }} /
                {{ number(task.total_records) }}
              </td>
              <td>
                <button
                  v-if="task.status === 'failed'"
                  class="btn btn-sm btn-outline"
                  :disabled="!canExport"
                  @click="retry(task)"
                >
                  Tạo lại {{ (task.format || "pdf").toUpperCase() }}</button
                ><button
                  v-else
                  class="btn btn-sm btn-outline"
                  :disabled="task.status !== 'done' || !!downloading"
                  @click="startDownload(task.id)"
                >
                  {{
                    downloading === task.id
                      ? "Đang tải…"
                      : "Tải " + (task.format || "pdf").toUpperCase()
                  }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <nav class="pager" aria-label="Phân trang lịch sử">
        <span>Trang {{ page }} / {{ totalPages }}</span
        ><button
          class="btn btn-sm btn-outline"
          :disabled="page <= 1 || isLoadingList"
          @click="setPage(page - 1)"
        >
          Lịch sử trước</button
        ><button
          class="btn btn-sm btn-outline"
          :disabled="page >= totalPages || isLoadingList"
          @click="setPage(page + 1)"
        >
          Lịch sử sau
        </button>
      </nav>
    </section>
    <dialog
      ref="sourceDialog"
      class="source-dialog"
      aria-labelledby="source-title"
      @close="selected = null"
      @click="$event.target === sourceDialog && sourceDialog?.close()"
    >
      <div v-if="selected" class="source-content">
        <div class="section-heading">
          <h2 id="source-title">Nguồn bản ghi</h2>
          <button
            class="btn btn-sm btn-outline"
            aria-label="Đóng nguồn"
            @click="sourceDialog?.close()"
          >
            Đóng
          </button>
        </div>
        <p class="identifier">
          {{ selected.id }} · {{ label(selected.data_type) }} ·
          {{ selected.agent_id }}
        </p>
        <pre>{{ JSON.stringify(selected.source, null, 2) }}</pre>
      </div>
    </dialog>
  </main>
</template>

<style scoped>
.report-page {
  padding: 24px;
  max-width: 1600px;
  margin: auto;
  min-width: 0;
  color: var(--color-base-content);
  background: var(--color-base-100);
  font-size: 14px;
  line-height: 1.5;
}
.report-page :is(.btn, .input, .select, .badge, .progress) {
  border-radius: 0;
}
.report-header,
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.report-header h1 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.025em;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.13em;
  color: #2563eb;
  font-weight: 700;
}
.report-header p:not(.eyebrow),
.muted,
.report-note {
  opacity: 0.7;
}
.exports {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.report-note {
  margin: 16px 0;
  font-size: 12px;
  border-left: 3px solid #2563eb;
  padding-left: 12px;
}
.filter-panel {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  padding: 18px;
  border: 1px solid var(--color-base-300);
  border-top: 3px solid #2563eb;
}
.filter-panel label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  min-width: 0;
}
.filter-panel :is(input, select) {
  width: 100%;
  min-width: 0;
  font-size: 13px;
  font-weight: 400;
}
.search-filter {
  grid-column: span 3;
}
.filter-panel > .btn {
  align-self: end;
}
.filter-feedback {
  grid-column: 1/-1;
}
.applied-scope {
  margin: 14px 0 20px;
  overflow-wrap: anywhere;
}
.applied-scope > span {
  font-size: 10px;
  letter-spacing: 0.1em;
  color: #2563eb;
  font-weight: 700;
}
.applied-scope p {
  font-size: 12px;
  opacity: 0.75;
}
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}
.kpi {
  border: 1px solid var(--color-base-300);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.kpi > span {
  font-size: 12px;
  opacity: 0.75;
}
.kpi strong {
  font-size: 28px;
  color: #2563eb;
  letter-spacing: -0.025em;
  overflow-wrap: anywhere;
}
.kpi small {
  font-size: 11px;
  opacity: 0.65;
}
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin: 20px 0;
}
.panel {
  border: 1px solid var(--color-base-300);
  padding: 20px;
  min-width: 0;
}
.panel h2,
.source-dialog h2 {
  font-size: 16px;
  font-weight: 650;
}
.muted {
  font-size: 12px;
  margin-top: 4px;
}
.trend-chart svg {
  width: 100%;
  height: auto;
  max-height: 220px;
  margin: 10px 0;
}
.daily-values {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
  max-height: 200px;
  overflow: auto;
  margin-top: 12px;
}
.distribution {
  display: grid;
  gap: 14px;
  margin-top: 20px;
}
.distribution li > div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.distribution meter {
  width: 100%;
  height: 8px;
  display: block;
  margin-top: 5px;
}
.distribution meter::-webkit-meter-bar {
  border: 0;
  border-radius: 0;
  background: var(--color-base-200);
}
.distribution meter::-webkit-meter-optimum-value {
  background: #2563eb;
}
.agent-rank {
  margin-top: 16px;
  display: grid;
  gap: 8px;
  counter-reset: rank;
}
.agent-rank li {
  display: flex;
  gap: 12px;
  border-bottom: 1px solid var(--color-base-200);
  padding: 8px 0;
  align-items: start;
}
.agent-rank li:before {
  counter-increment: rank;
  content: counter(rank, decimal-leading-zero);
  color: #2563eb;
  font-size: 12px;
}
.agent-rank span {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.agent-rank b {
  font-variant-numeric: tabular-nums;
}
.empty-chart,
.state-panel {
  padding: 30px 0;
  opacity: 0.7;
}
.table-scroll {
  overflow-x: auto;
  max-width: 100%;
  margin-top: 16px;
}
.table {
  width: 100%;
}
.table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.table td {
  vertical-align: top;
  font-size: 12px;
}
.identifier {
  overflow-wrap: anywhere;
  min-width: 120px;
  max-width: 260px;
}
.table .btn {
  white-space: nowrap;
}
.pager {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  font-size: 12px;
}
.history {
  margin-top: 20px;
}
.history-scope {
  min-width: 210px;
  max-width: 350px;
  overflow-wrap: anywhere;
}
.history-scope small {
  display: block;
  opacity: 0.65;
  margin-top: 5px;
}
.task-progress {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
}
.task-progress progress {
  width: 90px;
}
.source-dialog {
  width: min(800px, 95vw);
  max-height: 85vh;
  margin: auto;
  padding: 0;
  border: 1px solid var(--color-base-300);
  color: var(--color-base-content);
  background: var(--color-base-100);
}
.source-dialog::backdrop {
  background: #0f172a99;
}
.source-content {
  padding: 24px;
  overflow: auto;
}
.source-content pre {
  margin-top: 18px;
  padding: 16px;
  background: var(--color-base-200);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 12px;
}
summary {
  cursor: pointer;
  font-size: 12px;
  margin-top: 8px;
}
button:focus-visible,
input:focus-visible,
select:focus-visible,
summary:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}
@media (max-width: 900px) {
  .report-page {
    padding: 16px;
  }
  .report-header {
    align-items: start;
    flex-direction: column;
  }
  .kpi-grid,
  .filter-panel {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .search-filter {
    grid-column: 1/-1;
  }
  .chart-grid {
    grid-template-columns: 1fr;
  }
  .section-heading {
    align-items: start;
    flex-wrap: wrap;
  }
}
@media (max-width: 480px) {
  .report-page {
    padding: 12px;
  }
  .filter-panel {
    grid-template-columns: 1fr;
  }
  .search-filter {
    grid-column: auto;
  }
  .kpi {
    padding: 12px;
  }
  .kpi strong {
    font-size: 23px;
  }
  .panel {
    padding: 14px;
  }
  .pager {
    flex-wrap: wrap;
  }
  .report-header h1 {
    font-size: 23px;
  }
}
</style>
